import PropTypes from 'prop-types';
import React, { useImperativeHandle, forwardRef } from 'react';
import { Link, useLocation, useHistory } from 'react-router-dom';
import styled from 'styled-components';
import Avatar from 'components/Avatar';
import ComingSoonModal from 'components/ComingSoonModal';
import DataTable from 'components/DataTable';
import useUserTable from '../hooks/useUserTable';

const UserCell = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const UserInfo = styled.div`
  min-width: 0;
`;

const NameLine = styled.div`
  font-weight: 600;
  font-size: 14px;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.textGray?.primary || '#ffffff'
      : props.theme.colors?.blueBlack || '#000033'};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  .middle,
  .suffix {
    color: ${(props) =>
      props.theme.name === 'dark'
        ? props.theme.colors?.text?.secondaryMuted || '#94A3B8'
        : '#94A3B8'};
    font-weight: 500;
  }

  .suffix {
    margin-left: 2px;
  }
`;

const MetaLine = styled.div`
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.text?.secondaryMuted || '#94A3B8'
      : '#94A3B8'};
  font-size: 12.5px;
  margin-top: 3px;
  display: flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
  flex-wrap: wrap;
`;

const MetaDot = styled.span`
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background: currentColor;
  opacity: 0.6;
  flex-shrink: 0;
`;

const SinceChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;

  svg {
    width: 11px;
    height: 11px;
    opacity: 0.85;
  }
`;

const TsLink = styled(Link)`
  display: inline-flex;
  flex-direction: column;
  gap: 1px;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.textGray?.primary || '#ffffff'
      : props.theme.colors?.blueBlack || '#000033'};
  text-decoration: none;
  font-size: 13px;
  line-height: 1.35;
  white-space: nowrap;
  transition: color 0.15s ease;

  .ts-date-row {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }

  .ts-date {
    font-weight: 500;
  }

  .ts-chevron {
    width: 12px;
    height: 12px;
    opacity: 0.5;
    transition: opacity 0.15s ease;
    flex-shrink: 0;
  }

  .ts-time {
    font-size: 12px;
    opacity: 0.6;
  }

  &:hover,
  &:focus {
    color: ${(props) => props.theme.colors.primary};

    .ts-chevron {
      opacity: 0.9;
    }
  }
`;

const Dash = styled.span`
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.text?.secondaryMuted || '#94A3B8'
      : '#94A3B8'};
`;

const CalendarIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const ViewIcon = () => (
  <svg
    width="16"
    height="16"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    viewBox="0 0 24 24"
  >
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const TransactionsIcon = () => (
  <svg
    width="16"
    height="16"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    viewBox="0 0 24 24"
  >
    <path d="M17 1l4 4-4 4" />
    <path d="M3 11V9a4 4 0 0 1 4-4h14" />
    <path d="M7 23l-4-4 4-4" />
    <path d="M21 13v2a4 4 0 0 1-4 4H3" />
  </svg>
);

const ChatBubbleIcon = () => (
  <svg
    width="16"
    height="16"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    viewBox="0 0 24 24"
  >
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);

// Format phone number to (323) 842-1428 format
const formatPhone = (phone) => {
  if (!phone) return '';
  const cleaned = ('' + phone).replace(/\D/g, '');
  const match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    return `(${match[2]}) ${match[3]}-${match[4]}`;
  }
  return phone;
};

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value || 0);
};

const WEEKDAY = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

const formatPurchaseDate = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const day = WEEKDAY[d.getDay()];
  const month = MONTH[d.getMonth()];
  return `${day}, ${month} ${d.getDate()}, ${d.getFullYear()}`;
};

const formatPurchaseTime = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  let hours = d.getHours();
  const minutes = d.getMinutes();
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;
  const mm = minutes < 10 ? `0${minutes}` : `${minutes}`;
  return `${hours}:${mm} ${ampm}`;
};

const UserTable = forwardRef(({ searchTerm = '', roleFilter = '' }, ref) => {
  const location = useLocation();
  const history = useHistory();
  const { pathname } = location;

  // Determine route prefix based on current path
  const isTrackAdmin = pathname?.includes('admin-track');
  const isEmployeeAdmin = pathname?.includes('admin-employee');
  const routePrefix = isTrackAdmin
    ? '/admin-track'
    : isEmployeeAdmin
    ? '/admin-employee'
    : '/admin';

  const {
    users,
    loading,
    error,
    currentPage,
    setCurrentPage,
    totalPages,
    totalCount,
    appliedFilters,
  } = useUserTable(searchTerm, roleFilter);

  useImperativeHandle(ref, () => ({
    getAppliedFilters: () => appliedFilters,
    getTotalCount: () => totalCount,
  }));

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const renderName = (user) => {
    const last = user.last_name || '';
    const first = user.first_name || '';
    return (
      <>
        {last}
        {last && first ? ', ' : ''}
        {first}
        {user.middle_name ? (
          <span className="middle"> {user.middle_name}</span>
        ) : null}
        {user.suffix ? <span className="suffix">, {user.suffix}</span> : null}
      </>
    );
  };

  const renderLastPurchase = (user) => {
    const dateLabel = formatPurchaseDate(user.last_purchase_at);
    const timeLabel = formatPurchaseTime(user.last_purchase_at);
    if (!dateLabel || !user.last_purchase_id) {
      return <Dash>—</Dash>;
    }
    return (
      <TsLink
        to={`${routePrefix}/transactions/detail/${user.last_purchase_id}`}
      >
        <span className="ts-date-row">
          <span className="ts-date">{dateLabel}</span>
          <svg
            className="ts-chevron"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </span>
        <span className="ts-time">{timeLabel}</span>
      </TsLink>
    );
  };

  const columns = [
    {
      field: 'name',
      header: 'User',
      render: (user) => (
        <UserCell>
          <Avatar firstName={user.first_name} lastName={user.last_name} />
          <UserInfo>
            <NameLine>{renderName(user)}</NameLine>
            <MetaLine>
              <span>
                {user.cellphone ? formatPhone(user.cellphone) : 'No phone'}
              </span>
              {user.customer_since && (
                <>
                  <MetaDot />
                  <SinceChip>
                    <CalendarIcon />
                    {user.customer_since}
                  </SinceChip>
                </>
              )}
            </MetaLine>
          </UserInfo>
        </UserCell>
      ),
    },
    {
      field: 'events_attended_total',
      header: 'Events',
      render: (user) => user.events_attended_total || 0,
    },
    {
      field: 'lifetime_value_total',
      header: 'LTV',
      render: (user) => formatCurrency(user.lifetime_value_total),
    },
    {
      field: 'last_purchase_at',
      header: 'Last Purchase',
      render: renderLastPurchase,
    },
  ];

  // View routes to the same destination as Edit for now (no dedicated user profile page yet).
  const actions = [
    {
      type: 'view',
      title: 'View',
      mobileLabel: 'View',
      icon: ViewIcon,
      to: (user) => `${routePrefix}/users/edit/${user.id}`,
    },
    {
      type: 'transactions',
      title: 'Transactions',
      mobileLabel: 'Transactions',
      icon: TransactionsIcon,
      to: (user) => `${routePrefix}/users/transactions/${user.id}`,
    },
    {
      type: 'credit',
      title: 'Add Credit',
      mobileLabel: 'Add Credit',
      onClick: (user) =>
        history.push(`${routePrefix}/users/issue-credit/${user.id}`),
    },
    {
      type: 'message',
      title: 'Message',
      mobileLabel: 'Message',
      icon: ChatBubbleIcon,
      wrapper: ComingSoonModal,
      wrapperProps: {
        title: 'User Messaging',
        description:
          "We're building a powerful messaging system to help you connect with your users.",
        features: [
          'Message an individual user directly',
          'View history of all past conversations',
          'Get notifications and inbox for user messages',
        ],
      },
    },
    {
      type: 'edit',
      title: 'Edit',
      mobileLabel: 'Edit',
      to: (user) => `${routePrefix}/users/edit/${user.id}`,
    },
  ];

  const mobileQuickInfo = [
    {
      field: 'events_attended_total',
      label: 'Events',
      render: (user) => user.events_attended_total || 0,
    },
    {
      field: 'lifetime_value_total',
      label: 'LTV',
      render: (user) => formatCurrency(user.lifetime_value_total),
    },
    {
      field: 'last_purchase_at',
      label: 'Last Purchase',
      render: renderLastPurchase,
    },
  ];

  const emptyIcon = (
    <svg
      width="48"
      height="48"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      style={{ marginBottom: '16px', opacity: 0.5 }}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.5"
        d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
      />
    </svg>
  );

  if (error) {
    return (
      <div
        style={{
          textAlign: 'center',
          padding: '40px 0',
          color: '#EF4444',
        }}
      >
        Error loading users: {error.message}
      </div>
    );
  }

  return (
    <DataTable
      columns={columns}
      data={users}
      loading={loading}
      keyField="id"
      emptyMessage="No users found"
      emptySubtext="Try adjusting your filters or search terms"
      emptyIcon={emptyIcon}
      actions={actions}
      currentPage={currentPage}
      totalPages={totalPages}
      onPageChange={handlePageChange}
      skeletonRows={8}
      mobileQuickInfo={mobileQuickInfo}
    />
  );
});

UserTable.propTypes = {
  searchTerm: PropTypes.string,
  roleFilter: PropTypes.string,
};

export default UserTable;
